import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, SearchIcon, UserIcon, BuildingIcon, LogOutIcon, SettingsIcon, ChevronDownIcon, CoinsIcon, LayoutDashboardIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
interface HeaderProps {
  onMenuClick: () => void;
}
export function Header({
  onMenuClick
}: HeaderProps) {
  const {
    logout
  } = useAuth();
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Dati fittizi per le aziende
  const companies = [{
    id: '1',
    name: 'Acme Srl',
    selected: true
  }, {
    id: '2',
    name: 'TechSolutions Spa',
    selected: false
  }, {
    id: '3',
    name: 'Industria Meccanica Srl',
    selected: false
  }, {
    id: '4',
    name: 'Logistica Express Srl',
    selected: false
  }, {
    id: '5',
    name: 'Alimentari Freschi Spa',
    selected: false
  }, {
    id: '6',
    name: 'Edilizia Moderna Srl',
    selected: false
  }, {
    id: '7',
    name: 'GreenEnergy Spa',
    selected: false
  }, {
    id: '8',
    name: 'Retail Fashion Srl',
    selected: false
  }];
  // Filtra le aziende in base al termine di ricerca
  const filteredCompanies = companySearchTerm ? companies.filter(company => company.name.toLowerCase().includes(companySearchTerm.toLowerCase())) : companies;
  // Gestisce i click fuori dal dropdown per chiuderlo
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Focus sulla ricerca quando si apre il dropdown
  useEffect(() => {
    if (showCompanyDropdown && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showCompanyDropdown]);
  return <header className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow-sm border-b border-gray-100" data-id="element-834">
      <button type="button" className="px-4 text-gray-500 md:hidden" onClick={onMenuClick} data-id="element-835">
        <span className="sr-only" data-id="element-836">Apri menu</span>
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-837">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-838" />
        </svg>
      </button>
      <div className="flex-1 px-4 flex justify-between" data-id="element-839">
        <div className="flex-1 flex" data-id="element-840">
          <div className="w-full flex md:ml-0" data-id="element-841">
            <label htmlFor="search-field" className="sr-only" data-id="element-842">
              Cerca
            </label>
            <div className="relative w-full text-gray-400 focus-within:text-gray-600" data-id="element-843">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3" data-id="element-844">
                <SearchIcon className="h-5 w-5" aria-hidden="true" data-id="element-845" />
              </div>
              <input id="search-field" className="block w-full h-full pl-10 pr-3 py-2 border-transparent text-gray-900 placeholder-gray-500 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:border-[#7B68EE] focus:ring-1 focus:ring-[#7B68EE] sm:text-sm transition-all" placeholder="Cerca fattura, cliente, P.IVA, IBAN, PEC..." type="search" data-id="element-846" />
            </div>
          </div>
        </div>
        <div className="ml-4 flex items-center md:ml-6" data-id="element-847">
          {/* Credits indicator */}
          <div className="flex items-center mr-4 bg-gradient-to-r from-[#7B68EE] to-[#1DA1F2] px-3 py-1.5 rounded-full text-white" data-id="element-848">
            <CoinsIcon className="h-4 w-4 mr-1" data-id="element-849" />
            <span className="text-sm font-medium" data-id="element-850">125 crediti</span>
          </div>
          {/* Company selector */}
          <div className="relative" ref={dropdownRef} data-id="element-851">
            <button type="button" className="max-w-xs bg-white flex items-center text-sm rounded-lg p-1.5 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#7B68EE] transition-colors" onClick={() => setShowCompanyDropdown(!showCompanyDropdown)} data-id="element-852">
              <span className="sr-only" data-id="element-853">Cambia azienda</span>
              <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-854" />
              <span className="ml-2 text-gray-700 hidden md:inline-block" data-id="element-855">
                Acme Srl
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-400 ml-1" data-id="element-856" />
            </button>
            {showCompanyDropdown && <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-xl shadow-lg py-2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-20 border border-gray-100" data-id="element-857">
                <div className="px-4 py-2 border-b border-gray-100" data-id="element-858">
                  <div className="relative rounded-md" data-id="element-859">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-860">
                      <SearchIcon className="h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-861" />
                    </div>
                    <input type="text" ref={searchRef} className="focus:ring-[#7B68EE] focus:border-[#7B68EE] block w-full pl-10 sm:text-sm border-gray-200 rounded-lg" placeholder="Cerca azienda..." value={companySearchTerm} onChange={e => setCompanySearchTerm(e.target.value)} data-id="element-862" />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto py-1" data-id="element-863">
                  {filteredCompanies.length > 0 ? filteredCompanies.map(company => <a key={company.id} href="#" className={`block px-4 py-2 text-sm ${company.selected ? 'bg-[#7B68EE]/10 text-[#7B68EE]' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setShowCompanyDropdown(false)} data-id="element-864">
                        <div className="flex items-center" data-id="element-865">
                          <BuildingIcon className="h-4 w-4 mr-2 text-gray-400" data-id="element-866" />
                          {company.name}
                        </div>
                      </a>) : <div className="px-4 py-2 text-sm text-gray-500" data-id="element-867">
                      Nessuna azienda trovata
                    </div>}
                </div>
              </div>}
          </div>
          {/* Notification dropdown */}
          <button type="button" className="ml-3 bg-white p-1.5 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#7B68EE] transition-colors" data-id="element-868">
            <span className="sr-only" data-id="element-869">Visualizza notifiche</span>
            <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-870" />
          </button>
          {/* Profile dropdown */}
          <div className="ml-3 relative" data-id="element-871">
            <button type="button" className="max-w-xs bg-white flex items-center text-sm rounded-lg p-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#7B68EE] transition-colors" onClick={() => setShowUserDropdown(!showUserDropdown)} data-id="element-872">
              <span className="sr-only" data-id="element-873">Apri menu utente</span>
              <div className="h-8 w-8 rounded-lg bg-gradient-to-r from-[#7B68EE] to-[#1DA1F2] flex items-center justify-center overflow-hidden" data-id="element-874">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4FCizwzsrXpd7oSKvMhBGd/1555726299065.jpg" alt="Profilo utente" className="h-8 w-8 object-cover" data-id="element-875" />
              </div>
            </button>
            {showUserDropdown && <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg py-2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-20 border border-gray-100" data-id="element-876">
                <Link to="/profilo" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserDropdown(false)} data-id="element-877">
                  Il tuo profilo
                </Link>
                <Link to="/impostazioni-utente" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserDropdown(false)} data-id="element-878">
                  <div className="flex items-center" data-id="element-879">
                    <SettingsIcon className="h-4 w-4 mr-2" data-id="element-880" />
                    Impostazioni
                  </div>
                </Link>
                <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserDropdown(false)} data-id="element-881">
                  <div className="flex items-center" data-id="element-882">
                    <LayoutDashboardIcon className="h-4 w-4 mr-2" data-id="element-883" />
                    Backend Admin
                  </div>
                </Link>
                <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-884">
                  <div className="flex items-center" data-id="element-885">
                    <LogOutIcon className="h-4 w-4 mr-2" data-id="element-886" />
                    Logout
                  </div>
                </button>
              </div>}
          </div>
        </div>
      </div>
    </header>;
}